import styled from "styled-components"

import { Skeleton, spacing } from "@minuthq/meatball-ui-react"

import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  useFetchTaskAutomations,
  usePutTaskAutomation,
} from "src/data/taskManagement/taskAutomationQueries"
import { TTaskAutomationKey } from "src/data/taskManagement/taskAutomationTypes"
import { useFetchTaskTemplates } from "src/data/taskManagement/taskTemplateQueries"
import { useTranslate } from "src/i18n/useTranslate"

import { TaskAutomationCard } from "./TaskAutomationCard"

export function TaskAutomations() {
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()

  const fetchTaskAutomations = useFetchTaskAutomations({
    orgId,
  })

  const fetchTemplates = useFetchTaskTemplates({
    orgId,
    filters: {
      limit: 100,
      offset: 0,
    },
  })

  const putTaskAutomation = usePutTaskAutomation()

  function updateTaskAutomation(
    key: TTaskAutomationKey,
    enabled: boolean,
    templateId: string | undefined
  ) {
    if (!fetchTaskAutomations.data) {
      return
    }

    putTaskAutomation.mutate({
      path: {
        organization_id: orgId,
      },
      body: {
        ...fetchTaskAutomations.data,
        [key]: {
          enabled,
          template_id: templateId,
        },
      },
    })
  }

  const isLoading = fetchTaskAutomations.isLoading

  if (isLoading) {
    return <TaskAutomationSkeleton />
  }

  if (!fetchTaskAutomations.data) {
    return null
  }

  return (
    <CardsContainer>
      <TaskAutomationCard
        title={t(langKeys.task_management_automation_device_battery_low_title)}
        description={t(
          langKeys.task_management_automation_device_battery_low_description
        )}
        taskAutomation={fetchTaskAutomations.data.device_battery_low}
        templates={fetchTemplates.data?.templates ?? []}
        isLoading={isLoading}
        onSubmit={(enabled, templateId) =>
          updateTaskAutomation("device_battery_low", enabled, templateId)
        }
        automationKey="device_battery_low"
      />

      <TaskAutomationCard
        title={t(langKeys.task_management_automation_home_unmonitored_title)}
        description={t(
          langKeys.task_management_automation_home_unmonitored_description
        )}
        taskAutomation={fetchTaskAutomations.data.home_unmonitored}
        templates={fetchTemplates.data?.templates ?? []}
        isLoading={isLoading}
        onSubmit={(enabled, templateId) =>
          updateTaskAutomation("home_unmonitored", enabled, templateId)
        }
        automationKey="home_unmonitored"
      />

      <TaskAutomationCard
        title={t(langKeys.task_management_automation_checkout_clean_title)}
        description={t(
          langKeys.task_management_automation_checkout_clean_description
        )}
        taskAutomation={fetchTaskAutomations.data.guest_checkout}
        templates={fetchTemplates.data?.templates ?? []}
        isLoading={isLoading}
        onSubmit={(enabled, templateId) =>
          updateTaskAutomation("guest_checkout", enabled, templateId)
        }
        automationKey="guest_checkout"
      />
    </CardsContainer>
  )
}

function TaskAutomationSkeleton() {
  const height = 150
  return (
    <CardsContainer>
      <Skeleton variant="rect" height={height} />
      <Skeleton variant="rect" height={height} />
      <Skeleton variant="rect" height={height} />
    </CardsContainer>
  )
}

const CardsContainer = styled.div`
  display: grid;
  gap: ${spacing.M};
`
